<template>
    <div class="chat-trace">
        <div v-if="isCollapsed" class="collapsed-toggle" @click="expand">
            <span>Agent Thought Trace</span>
            <q-icon name="expand_more" size="14px" class="q-ml-xs"/>
        </div>

        <template v-else>
            <div class="trace-header q-mb-sm" :class="{ 'is-clickable': hasFinished }" @click="collapse">
                <span>{{ header }}</span>
                <q-icon v-if="hasFinished" name="expand_less" size="14px" class="q-ml-xs"/>
            </div>

            <div v-for="(step, index) in processedData" :key="index" class="step-container">
                <div class="line-gutter">
                    <div class="line-path q-mt-xs" v-if="index < processedData.length - 1 || step.is_expanded"></div>
                    <div class="icon-node"></div>
                </div>
                <div class="step-content">
                    <div class="step-header" @click="toggle(index)" :style="`padding-bottom: ${step.is_expanded || index == processedData.length - 1 ? '0' : '10'}px;`">
                        <div class="flex items-center">
                            <span class="step-name">{{ step.step_name }}</span>
                            <q-icon v-if="step.details && step.details.length > 0" name="expand_more" size="14px" class="q-ml-xs step-chevron" :class="{ 'rotate180': step.is_expanded }"/>
                        </div>
                    </div>
                    <div v-if="step.is_expanded" class="details-section" :style="`padding-bottom: ${index < processedData.length - 1 ? '6' : '0'}px;`">
                        <div v-for="(detail, detailIndex) in step.details" :key="detailIndex" class="detail-item">
                            <div class="detail-icon-node"></div>
                            <div class="detail-content">
                                <template v-for="(line, lineIndex) in getNonCodeText(detail).split('\n')" :key="lineIndex">
                                    <div v-html="markdownToHtml(line)" class="detail-text"></div>
                                </template>
                                <template v-if="detail.includes('```python') && detail.includes('```')">
                                    <highlightjs language="python" :code="extractPythonCode([detail])" class="python-code"></highlightjs>
                                </template>
                                <template v-if="detail.includes('```sql') && detail.includes('```')">
                                    <highlightjs language="sql" :code="extractSQLCode([detail])" class="python-code"></highlightjs>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import 'highlight.js/lib/common';
import hljsVuePlugin from "@highlightjs/vue-plugin";

export default {

    name: "thought-trace",

    emits: ["setscroll"],

    components: {
        "highlightjs": hljsVuePlugin.component,
    },

    props: {
        data: {
            type: Array,
            required: true,
            default: () => [],
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        // The critic still runs after the answer has streamed in, but the trace has served its
        // purpose by then, so it folds away without waiting for the stream to close.
        answered: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            processedData: [],
            isAnimating: false,
            // A trace loaded from an older chat is already complete, so it starts folded away.
            isCollapsed: !this.loading || this.answered,
            // A new step restarts the reveal while the previous step's details are still
            // playing; the timers of that run resolve against a stale token and stop there.
            runToken: 0,
        };
    },

    computed: {
        header() {
            return this.data.find(step => step?.header)?.header || "Agent thought trace";
        },

        isStreaming() {
            return this.loading && !this.answered;
        },

        hasFinished() {
            return !this.isStreaming && !this.isAnimating;
        },
    },

    watch: {
        data: {
            handler(newVal, oldVal) {
                if(newVal?.length !== oldVal?.length){
                    this.processData()
                }
            },
            deep: true,
            immediate: true
        },

        // Cuts short a reveal still in progress so the trace collapses the moment the answer lands.
        isStreaming(streaming) {
            if (!streaming) {
                this.processData();
            }
        },

        hasFinished(finished) {
            if (finished) {
                this.collapse();
            }
        },
    },

    beforeUnmount() {
        this.runToken += 1;
    },

    methods: {
        toggle(index) {
            const step = this.processedData[index];
            if (step.details && step.details.length > 0) {
                step.is_expanded = !step.is_expanded;
            }
        },

        expand() {
            this.isCollapsed = false;
        },

        collapse() {
            if (!this.hasFinished) {
                return;
            }
            this.processedData.forEach(step => {
                step.is_expanded = false;
            });
            this.isCollapsed = true;
        },

        async processData() {
            const token = ++this.runToken;
            if(this.isStreaming) {
                this.processedData = this.data.slice(0, -1).map((step) => {
                    return {
                        step_name: step.step_name,
                        is_expanded: true,
                        details: step.details || [],
                    };
                });

                if (this.data.length > 0) {
                    const lastStep = this.data[this.data.length - 1];
                    const details = lastStep.details || [];
                    this.processedData.push({
                        step_name: lastStep.step_name,
                        is_expanded: true,
                        details: [],
                    });
                    // Read back through the array so the details land on the reactive copy.
                    const visibleStep = this.processedData[this.processedData.length - 1];

                    this.isAnimating = true;
                    for (let i=0; i<details.length; i++) {
                        await new Promise((res) => setTimeout(res, 800));
                        if (token !== this.runToken) {
                            return;
                        }
                        visibleStep.details = details.slice(0, i+1);
                        this.$emit("setscroll");
                    }
                }
                this.isAnimating = false;
            } else {
                this.isAnimating = false;
                this.processedData = this.data.map((step) => {
                    return {
                        step_name: step.step_name,
                        is_expanded: false,
                        details: step.details || [],
                    };
                });
            }
        },
        
        markdownToHtml(markdown) {
            markdown = markdown.replace(/\*\*(.*?)\*\*/g, '<b style="font-weight: 600;">$1</b>');
            markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<i>$1</i>');
            markdown = markdown.replace(/^- (.*$)/gm, '<li style="margin-left:14px; font-size: 11px; line-height: 16px;">$1</li>');
            markdown = markdown.replace(/(<li style="margin:0;">[\s\S]*?<\/li>)+/g, '<ul style="margin:0; font-size: 11px; line-height: 16px;">$&</ul>');
            markdown = markdown.replace(/<\/ul>\s*<ul style="margin:0;">/g, '');
            return markdown;
        },

        extractPythonCode(textArray) {
            const fullText = textArray.join('\n');
            const pythonCodeMatch = fullText.match(/```python\n?([\s\S]*?)```/);
            return pythonCodeMatch ? pythonCodeMatch[1].trim() : '';
        },

        extractSQLCode(textArray) {
            const fullText = textArray.join('\n');
            const sqlCodeMatch = fullText.match(/```sql\n?([\s\S]*?)```/);
            return sqlCodeMatch ? sqlCodeMatch[1].trim() : '';
        },

        getNonCodeText(text) {
            return text.replace(/```python\n?[\s\S]*?```/g, '').replace(/```sql\n?[\s\S]*?```/g, '').trim();
        },
    },
};
</script>

<style scoped>
.chat-trace {
    width: 100%;
    min-width: 0;
}

.collapsed-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.collapsed-toggle:hover {
    color: #1f1f1f;
}

.trace-header {
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #424242;
}

.trace-header.is-clickable {
    cursor: pointer;
}

.trace-header.is-clickable:hover {
    color: #1f1f1f;
}

.step-container {
    display: flex;
    position: relative;
    width: 100%;
    min-width: 0;
}

.line-gutter {
    flex-shrink: 0;
    width: 16px;
    position: relative;
    display: flex;
    justify-content: center;
}

.line-path {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background-image: linear-gradient(to bottom, #f3b699 40%, transparent 0%);
    background-size: 1px 6px;
    background-repeat: repeat-y;
}

.step-container.is-last .line-path {
    height: 10px;
}

.icon-node {
    width: 7px;
    height: 7px;
    background-color: #ff4e00;
    border-radius: 50%;
    margin-top: 4px;
    z-index: 1;
}

.step-content {
    flex-grow: 1;
    min-width: 0;
}

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    min-height: 16px;
}

.step-name {
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.step-header:hover .step-name,
.step-header:hover .step-chevron {
    color: #1f1f1f;
}

.step-chevron {
    color: #757575;
    transition: transform 0.15s ease;
}

.rotate180 {
    transform: rotate(180deg);
}

.details-section {
    margin-top: 4px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-left: 2px;
    margin-top: 6px;
    min-width: 0;
}

.detail-icon-node {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    background-color: #f5966e;
    border-radius: 50%;
    margin-right: 8px;
    margin-top: 5px;
}

.detail-content {
    min-width: 0;
    line-height: 1.5;
    color: #5f5f5f;
}

.detail-text {
    font-size: 11px;
    line-height: 16px;
    overflow-wrap: anywhere;
}

.python-code {
    display: block;
    width: 100%;
    max-width: 100%;
    max-height: 200px;
    margin: 6px 0 0;
    font-family: monospace;
    font-size: 10px;
    border: 1px solid #ffd3c0;
    border-radius: 6px;
    background: #ffffff;
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: #f3b699 transparent;
}

/* The hljs theme puts overflow-x on the inner <code>; keep a single scroll
   container on the <pre> so max-height can clip vertically. max-content keeps
   the padding and background spanning the full width while scrolled. */
.python-code :deep(.hljs) {
    display: block;
    width: max-content;
    min-width: 100%;
    padding: 8px !important;
    background: transparent !important;
    overflow: visible !important;
    white-space: pre;
}

.python-code::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.python-code::-webkit-scrollbar-track {
    background: transparent;
}

.python-code::-webkit-scrollbar-thumb {
    background: #f3b699;
    border-radius: 3px;
}

.python-code::-webkit-scrollbar-thumb:hover {
    background: #ff4e00;
}

.python-code::-webkit-scrollbar-corner {
    background: transparent;
}
</style>
